import { authClient } from "@/lib/authClient";
import { RouteTable } from "@/lib/routes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@repo/ui/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/Card";
import { FieldGroup } from "@repo/ui/components/Field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/Form";
import { Input } from "@repo/ui/components/Input";
import { LoadingWrapper } from "@repo/ui/components/LoadingWrapper";
import { toast } from "@repo/ui/lib/toast";
import { useMutation } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";
import { setPasswordSchema, type SetPasswordFormData } from "./formSchema";

const defaultValues: SetPasswordFormData = {
  currentPassword: "",
  password: "",
  confirmPassword: "",
};

export const ChangePassword = () => {
  const navigate = useNavigate();
  const { data: session, refetch: refetchSession } = authClient.useSession();

  const form = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues,
  });

  const { mutate: changePassword, isPending } = useMutation({
    mutationFn: async ({ currentPassword, password }: SetPasswordFormData) => {
      const response = await authClient.changePassword({
        currentPassword,
        newPassword: password,
        revokeOtherSessions: false,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to change password");
      }

      return response;
    },
    onSuccess: async () => {
      await refetchSession();

      toast.success("Password changed successfully");

      form.reset();
      void navigate(RouteTable.dashboard());
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (!session) {
    return <Navigate to={RouteTable.login()} />;
  }

  return (
    <div className="flex items-center justify-center p-6 md:p-10">
      <div className="flex w-full max-w-lg flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
          </CardHeader>
          <LoadingWrapper isLoading={isPending}>
            <CardContent>
              <Form {...form}>
                <form
                  noValidate
                  onSubmit={(e) => void form.handleSubmit((data) => changePassword(data))(e)}
                >
                  <FieldGroup>
                    <FormField
                      control={form.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Enter your current password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Create a strong password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Re-enter the new password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" className="w-full" disabled={isPending}>
                      {isPending ? (
                        <>
                          <Loader className="mr-2 size-4 animate-spin" />
                          Changing Password...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  </FieldGroup>
                </form>
              </Form>
            </CardContent>
          </LoadingWrapper>
        </Card>
      </div>
    </div>
  );
};
